/* eslint-disable no-nested-ternary */
/* eslint-disable max-lines */
import { useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import {
  Map,
  useRouter,
  useMediaQuery,
  useI18n,
  useAsync,
} from '@popety_io/popety-io-lib'
import mapboxgl from 'mapbox-gl'
import { useTheme } from '@mui/material'
import { estimateSelector, summaryService } from '../../../redux'
import FaoMapTilePopup from './FaoMapTilePopup'
import { buildingsFaoTiles, getFaoTiles, removeFaoTiles } from './fao.layers'

const fillStyle = 'layer_fill'

const FaoTiles = ({ map, loaded }: { map: Map; loaded?: boolean }) => {
  const { dispatch, data, loading } = useAsync<any[]>()
  const { t } = useI18n()
  const router = useRouter()
  const estimate: any = useSelector(estimateSelector)
  const [feature, setFeature] = useState<any>()
  const [coordinatesCurrent, setCoordinatesCurrent] = useState<any>()
  const [popup, setPopup] = useState<mapboxgl.Popup>()
  const [popupFao, setPopupFao] = useState<mapboxgl.Popup>()
  const touchScreen = useMediaQuery('(hover: none)')

  const theme = useTheme()

  const containerRef = useRef<any>(null)

  const color = theme?.palette?.primary?.main || '#00ade6'

  const isFaoTab =
    router?.query?.tab === 'fao' || router?.query?.subTab === 'fao'
  const { faoCenter, title } = router.query

  const query =
    estimate?.historyFilters &&
    encodeURIComponent(JSON.stringify(estimate?.historyFilters))
  const buildingQuery = encodeURIComponent(
    JSON.stringify({
      classification: 'projected',
      geoPoint: {
        lat: estimate?.historyFilters?.center?.[1],
        lon: estimate?.historyFilters?.center?.[0],
      },
      radius: estimate?.historyFilters?.radius || 0,
    }),
  )

  const handleClickItemPopup = () => {
    if (map && popupFao) {
      popupFao.remove()
    }
  }

  const handleClickMap = () => {
    if (map && popupFao) {
      popupFao.remove()
    }
  }

  const handleClick = (e: any) => {
    if (map && popupFao) {
      popupFao.remove()
    }

    dispatch(() =>
      summaryService.getFaosTileDetails(
        e.features.map(
          (f: { properties: { _key: string } }) => f.properties._key,
        ),
        query,
      ),
    )

    const coordinates = e.features[0].geometry.coordinates.slice()

    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
    }

    setCoordinatesCurrent(coordinates)
  }

  const handleMouseMove = (e: any) => {
    map.getCanvas().style.cursor = 'pointer'
    if (popup) {
      const coordinates = e.features[0].geometry.coordinates.slice()
      const str = t('land.history.faos')
      const description = `${e.features[0].properties._count} ${str}`

      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
      }

      popup.setLngLat(coordinates).setHTML(description).addTo(map)
    }
  }

  const handleMouseLeave = () => {
    map.getCanvas().style.cursor = ''

    if (popup) {
      popup.remove()
    }
  }

  const addFaoTiles = () => {
    try {
      buildingsFaoTiles({ map, query: buildingQuery })
      getFaoTiles({
        map,
        query,
        color,
      })
    } catch (e) {
      console.error(e)
    }
  }

  useEffect(() => {
    setPopup(
      new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
      }),
    )
    setPopupFao(
      new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        className: 'custom-popup-fao',
      }),
    )
  }, [])

  useEffect(() => {
    if (!isFaoTab && map && loaded) {
      removeFaoTiles({ map })
    }
  }, [isFaoTab])

  useEffect(() => {
    if (touchScreen) setPopup(undefined)
  }, [touchScreen])

  useEffect(() => {
    if (loading && popupFao) {
      popupFao.remove()
    }

    if (!isFaoTab && popupFao) {
      popupFao.remove()
      setCoordinatesCurrent(null)
    }

    if (
      !loading &&
      data &&
      coordinatesCurrent &&
      popupFao &&
      containerRef.current &&
      isFaoTab
    ) {
      if (data?.length === 0) {
        popupFao.remove()

        return
      }
      if (data?.length === 1) {
        router.updateQuery({
          faoId: data?.[0]?._id || data?.[0]?.id || null,
        })
      } else {
        popupFao
          .setLngLat(coordinatesCurrent)
          .setDOMContent(containerRef.current)
          .addTo(map)
      }
    }
  }, [data, loading, popupFao, coordinatesCurrent, router.query?.tab, isFaoTab])

  useEffect(() => {
    if (map && loaded && faoCenter && popup && title) {
      const [lng, lat] = faoCenter.split(',')

      map.flyTo({ center: [lng, lat], zoom: 17 })
      popup.setLngLat([lng, lat]).setHTML(title).addTo(map)
    } else if (map && loaded && !faoCenter && popup && !title) {
      popup.remove()
    }
  }, [faoCenter, map, loaded, popup, title])

  useEffect(() => {
    if (!map || !loaded || !isFaoTab) return

    addFaoTiles()

    map.on('style.load', addFaoTiles)
    map.on('click', fillStyle, handleClick)
    map.on('mousemove', fillStyle, handleMouseMove)
    map.on('mouseleave', fillStyle, handleMouseLeave)
    map.on('click', handleClickMap)

    return () => {
      map.off('style.load', addFaoTiles)
      map.off('click', fillStyle, handleClick)
      map.off('mousemove', fillStyle, handleMouseMove)
      map.off('mouseleave', fillStyle, handleMouseLeave)
      map.off('click', handleClickMap)
    }
  }, [isFaoTab, loaded, query, faoCenter, title])

  useEffect(() => {
    if (data?.length) {
      setFeature(data[0])
    }
  }, [data])

  const handleClose = () => {
    setFeature(null)
  }

  return (
    <FaoMapTilePopup
      data={data}
      feature={feature}
      handleClose={handleClose}
      ref={containerRef}
      loading={loading}
      onClick={handleClickItemPopup}
    />
  )
}

export default FaoTiles
